/* ============================================================
   SALES  (/sales-v3.9)  — SESAAT Studio v3.9
   ============================================================
   Rekod Jualan dalam bahasa reka bentuk v3.9.

   ⚠️ Menu "Sales" dalam StudioShell dahulunya menghala ke
   /admin/sales — route itu TIDAK WUJUD (route admin yang ada
   hanyalah /admin, /admin/affiliate, /admin/marketplace,
   /admin/automation). Menu itu tidak membuat apa-apa. Halaman
   ini menggantikannya.

   Ruangan penghantaran (Flow Ultra, Bind MCN, Add-on) ialah
   senarai tugas sebenar selepas bayaran diterima — bukan
   hiasan. Jangan buang lajur itu.

   ⚠️ PROTOTAIP — pesanan di bawah adalah contoh tetap.
   ============================================================ */

import { useMemo, useState } from 'react';
import { Search, Download, Check, Minus } from 'lucide-react';

import '../design/tokens.css';
import { Badge, Btn, PageHeader, Segmented, Field, EmptyState, StatTile } from '../design/ui';
import { useT } from '../i18n';

const PESANAN = [
    { id: 'ORD-10482', email: 'aliaqistina***@gmail.com', pakej: 'Premium', jumlah: 199, tarikh: '5 Ogos 2026', status: 'paid', mcn: true, flow: true, addon: false },
    { id: 'ORD-10481', email: 'nurhidayah***@gmail.com', pakej: 'Fighter', jumlah: 799, tarikh: '5 Ogos 2026', status: 'paid', mcn: false, flow: true, addon: true },
    { id: 'ORD-10479', email: 'faezmegat***@gmail.com', pakej: 'Premium', jumlah: 199, tarikh: '3 Ogos 2026', status: 'pending', mcn: false, flow: false, addon: false },
    { id: 'ORD-10476', email: 'wahida***@gmail.com', pakej: 'Premium + FLOWNITY', jumlah: 268, tarikh: '1 Ogos 2026', status: 'paid', mcn: true, flow: true, addon: true },
    { id: 'ORD-10471', email: 'zulhilmi***@gmail.com', pakej: 'Premium', jumlah: 199, tarikh: '29 Julai 2026', status: 'refund', mcn: false, flow: false, addon: false },
];

const wang = (n) => `RM ${n.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* Tanda siap / belum untuk tugasan penghantaran. */
function Tugas({ on }) {
    return on
        ? <Check size={14} strokeWidth={2.2} style={{ color: 'var(--s-ok)' }} />
        : <Minus size={14} strokeWidth={2} style={{ color: 'var(--s-ink-3)' }} />;
}

export default function SalesV39View() {
    const t = useT();
    const [tapis, setTapis] = useState('semua');
    const [cari, setCari] = useState('');

    const ditapis = useMemo(() => {
        const q = cari.trim().toLowerCase();
        return PESANAN
            .filter((o) => tapis === 'semua'
                || (tapis === 'belum' ? (o.status === 'paid' && !(o.mcn && o.flow)) : o.status === tapis))
            .filter((o) => !q || o.email.toLowerCase().includes(q) || o.id.toLowerCase().includes(q));
    }, [tapis, cari]);

    /* Hanya pesanan berbayar dikira sebagai hasil — pending belum
       masuk duit, refund sudah keluar semula. */
    const hasil = PESANAN.filter((o) => o.status === 'paid').reduce((s, o) => s + o.jumlah, 0);
    const belumSettle = PESANAN.filter((o) => o.status === 'paid' && !(o.mcn && o.flow)).length;

    return (
        <div className="sesaat-m-tight" style={{ padding: '26px 28px 48px' }}>

            <PageHeader
                title={t('sales.title')}
                subtitle={t('sales.subtitle')}
                actions={
                    <>
                        {/* ⛔ `value` = nilai penapis dalaman (dibanding dalam useMemo di atas).
                            Hanya `label` diterjemah — jangan sentuh `value`. */}
                        <Segmented
                            value={tapis}
                            onChange={setTapis}
                            options={[
                                { value: 'semua', label: t('sales.filter.all') },
                                { value: 'paid', label: t('sales.status.paid') },
                                { value: 'pending', label: t('sales.status.pending') },
                                { value: 'belum', label: t('sales.unsettled') },
                            ]}
                        />
                        <Btn icon={<Download size={15} strokeWidth={2} />}>{t('sales.exportCsv')}</Btn>
                    </>
                }
            />

            <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: 12, marginBottom: 22,
            }}>
                <StatTile label={t('sales.stat.revenue')} value={wang(hasil)} hint={t('sales.stat.revenueHint')} />
                <StatTile label={t('sales.stat.orders')} value={String(PESANAN.length)} />
                <StatTile label={t('sales.unsettled')} value={String(belumSettle)} hint={t('sales.stat.unsettledHint')} />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
                <Field
                    icon={<Search size={15} strokeWidth={1.9} />}
                    placeholder={t('sales.searchPlaceholder')}
                    value={cari}
                    onChange={(e) => setCari(e.target.value)}
                    style={{ maxWidth: 320 }}
                />
                <span className="s-dim" style={{ marginLeft: 'auto' }}>{t('sales.prototype')}</span>
            </div>

            {ditapis.length === 0 ? (
                <EmptyState title={t('sales.empty.title')}>
                    {t('sales.empty.body')}
                </EmptyState>
            ) : (
                <div className="s-list" style={{ overflowX: 'auto' }}>
                    <table className="s-table">
                        <thead>
                            <tr>
                                <th>{t('sales.th.order')}</th><th>{t('sales.th.customer')}</th>
                                <th>{t('sales.th.package')}</th><th>{t('sales.th.date')}</th>
                                <th>{t('common.status')}</th><th>{t('sales.th.flowUltra')}</th>
                                <th>{t('sales.th.bindMcn')}</th><th>{t('sales.th.addon')}</th>
                                <th className="s-td-num">{t('sales.th.amount')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ditapis.map((o) => (
                                <tr key={o.id}>
                                    <td className="s-num">{o.id}</td>
                                    <td className="s-td-strong">{o.email}</td>
                                    <td>{o.pakej}</td>
                                    <td>{o.tarikh}</td>
                                    <td>
                                        {o.status === 'paid' && <Badge tone="ok">{t('sales.status.paid')}</Badge>}
                                        {o.status === 'pending' && <Badge tone="warn">{t('sales.status.pending')}</Badge>}
                                        {o.status === 'refund' && <Badge tone="stop">{t('sales.status.refund')}</Badge>}
                                    </td>
                                    <td><Tugas on={o.flow} /></td>
                                    <td><Tugas on={o.mcn} /></td>
                                    <td><Tugas on={o.addon} /></td>
                                    <td className="s-td-num s-td-strong">{wang(o.jumlah)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

        </div>
    );
}
